import { BaseERC20 } from '@arcxgame/contracts/dist/src/typings'
import { useAsync } from '@arcxmoney/arcx-react'
import { BigNumber } from 'ethers'
import { useContext } from 'react'
import { getConfigForNetwork } from 'src/config'
import { ArcContext } from 'src/contexts/ArcContext'
import ArcFarm from 'src/models/ArcFarm'
import { ArcFarmFactory } from 'src/models/ArcFarmFactory'
import { IPeriodEndDate } from 'src/models/IPeriodEndDate'
import { IRewardPair } from 'src/models/IRewardPair'

interface ISingleFarmDetails {
  farm: ArcFarm
  pair: string
  pairLink: string
  note: string
  hasDebtRequirement: boolean
  stakableBalance: BigNumber
  stakedBalance: BigNumber
  debtDeadline: string
  debtRequirement: string
  syntheticAssetSymbol: string
  currentDebt: string
  availableToStake: BigNumber
  rewardTokens: string
  endDates: IPeriodEndDate[]
  earnings: IRewardPair[]
  stakingSymbol: string
  tokensClaimable: boolean
}

const emptyDetails: ISingleFarmDetails = {
  farm: null,
  pair: null,
  pairLink: null,
  note: null,
  hasDebtRequirement: null,
  debtDeadline: null,
  debtRequirement: null,
  syntheticAssetSymbol: null,
  currentDebt: null,
  availableToStake: null,
  rewardTokens: null,
  stakedBalance: null,
  endDates: null,
  earnings: null,
  stakingSymbol: null,
  stakableBalance: null,
  tokensClaimable: null,
}

export function useSingleFarmDetails(farmName: string): ISingleFarmDetails {
  const { arc, network, isPlaynetActivated, client } = useContext(ArcContext)
  const signer = arc?.signer

  const [farmDetails] = useAsync<ISingleFarmDetails>(async () => {
    if (!signer || !network) return emptyDetails

    const config = getConfigForNetwork(network, isPlaynetActivated)

    if (!config) {
      console.error(`Could not find config for farm ${farmName}`)
      return emptyDetails
    }

    const farms = config.rewardPools
    const farmDetails = farms.find((rewardPool) => rewardPool.name === farmName)

    if (!farmDetails) {
      console.error(`Could not find farm details for farm ${farmName}`)
      return emptyDetails
    }

    const arcFarm = ArcFarmFactory.new(farmDetails, signer, network, client, isPlaynetActivated)

    const rewardTokens = await arcFarm.getRewardTokens()
    const rewardTokensString = await _getRewardTokensString(rewardTokens)

    const stakingToken = await arcFarm.getStakingToken()
    const stakingSymbol = await stakingToken?.symbol()

    const earnings = await arcFarm.getEarnings()
    let tokensClaimable = await arcFarm.getIsRewardClaimable()

    let hasEarnings = false
    for (const earning of earnings) {
      if (earning.amount.gt(BigNumber.from(0))) {
        hasEarnings = true
        break
      }
    }

    tokensClaimable = tokensClaimable && hasEarnings

    try {
      return {
        farm: arcFarm,
        pair: arcFarm.pair,
        pairLink: arcFarm.pairLink,
        note: arcFarm.note,
        hasDebtRequirement: arcFarm.hasDebtRequirement(),
        stakedBalance: await arcFarm.getStakedBalance(),
        debtDeadline: await arcFarm.getVestingEndDateString(),
        debtRequirement: await arcFarm.getDebtRequirementString(),
        syntheticAssetSymbol: 'STABLEx',
        currentDebt: await arcFarm.getCurrentDebtString(),
        availableToStake: await arcFarm.getAvailableToStake(),
        rewardTokens: rewardTokensString,
        endDates: await arcFarm.getPeriodFinish(),
        earnings,
        stakingSymbol,
        stakableBalance: await arcFarm.getStakingTokenBalance(),
        tokensClaimable,
      }
    } catch (err) {
      console.warn(`Error getting the farm info for ${farmName}:`)
      console.error(err)
      return emptyDetails
    }
  }, [network, isPlaynetActivated, signer])

  if (!farmDetails) {
    return emptyDetails
  }

  return farmDetails
}

async function _getRewardTokensString(rewardTokens: BaseERC20[]): Promise<string> {
  let rewardTokensString = ''
  for (let i = 0; i < rewardTokens.length; i++) {
    const rewardToken = rewardTokens[i]
    const symbol = await rewardToken.symbol()

    rewardTokensString += symbol

    if (i + 1 < rewardTokens.length) {
      rewardTokensString += ' + '
    }
  }

  return rewardTokensString
}
